import React, {
  Fragment,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  lazy,
  Suspense
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SvgIcon from 'src/react_components/svg-icon';
import CitationPane from 'annotator/citation-pane';
import CommentInputPane from 'annotator/comment-input-pane';
import { AnnotatorObject } from 'annotator/annotator-types';
import {
  Annotation, CommentCategory, CommentViewExpandedProps, CategoryClassName, DisplayType
} from './annotator-types';
import { YambaRootState } from '../../../../yamba/assets/src/yamba/store';
import { BellmereQA } from './bellmere-qa/qa-types.d';
import CommentHelperList from './comment-helper-list';
import MultiParagraphDisplay from './multi-paragraph-display';
import { snakeToPascalCase, matchTimestamp } from './annotator-helpers';
import {
  toggleQuestionHighlight,
  getAddedSelector
} from '../../../../yamba/assets/src/yamba/slices/questionHighlightsSlice';

const QAActionState = lazy(() => import('./bellmere-qa/qa-action-state'));
const QAAlertMessage = lazy(() => import('./bellmere-qa/qa-alert-message'));
const QABooleanButtons = lazy(() => import('./bellmere-qa/qa-boolean-buttons'));

declare const Annotator: AnnotatorObject;

const CommentViewExpanded = ({
  annotation,
  annotationText,
  assignCommentVariants,
  bulkDeleteMode,
  cancelEdit,
  commentCategory,
  commentCategoryAndType,
  commentType,
  capitalizedCommentCategory,
  commentIcon,
  commentHelpersSummary,
  confirmingDeletion,
  contextWidth,
  editComment,
  handleRestoreAnnotation,
  hasSources,
  inDiscussion,
  isEditable,
  isEditing,
  isEditingDisabled,
  isPanelSelected,
  markedForDeletion,
  onCancelRemovingAnnotation,
  onMarkedForDeletionChange,
  onStartRemovingAnnotation,
  keyboardSelectHighlight,
  removeAnnotation,
  removeCitation,
  restorable,
  saveCiteStatus,
  saveComment,
  savingSubmission,
  selectedParagraphIndex,
  viewingAsBellmereQa,
  bellmereQAResults,
  setBellmereQAResult,
  bellmereQAEditable
}: CommentViewExpandedProps) => {
  const qaUpdated = (result: BellmereQA.AnnotationResult) => {
    setShowQAButtons(
      result.action === BellmereQA.ActionState.HesitateToShow ||
      result.action === BellmereQA.ActionState.DoNotShow
    );

    setBellmereQAResult(result);
  };

  let addedToYamba: boolean;
  let questionPaneVisible: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dispatch: any;

  try {
    addedToYamba = useSelector(getAddedSelector(annotation.timestamp));
    questionPaneVisible = useSelector((state: YambaRootState) => state.questionPanel.visible);
    dispatch = useDispatch();
  } catch (error) {
    // No Provider is available so we are probably in the practice submission
    addedToYamba = false;
    questionPaneVisible = false;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dispatch = () => { }; // Fallback dispatch function that does nothing
  }

  const toggleHighlight = () => {
    if (!Annotator.showYambaButton) return;

    const { timestamp } = annotation;

    if (!questionPaneVisible) {
      window.location.hash = '/connect-live';
    }

    dispatch(toggleQuestionHighlight({ timestamp }));
  };

  const result: BellmereQA.AnnotationResult =
    bellmereQAResults?.find((r) => r.submission_annotation_id === String(annotation.id)) ||
    {
      submission_annotation_id: String(annotation.id),
      action: null,
      highlight_range_correct: null,
      areas_for_improvement: [],
      example_quality_acceptable: null,
      explanation_quality_acceptable: null,
      comments: ''
    };

  const [showQAButtons, setShowQAButtons] = useState<boolean>(
    result.action === BellmereQA.ActionState.HesitateToShow ||
    result.action === BellmereQA.ActionState.DoNotShow
  );

  // Popover to show help for Yamba buttons
  const popoverRef = useRef(null);
  const commentViewExpanedRef = useRef(null);
  let questionHighlights;

  try {
    questionHighlights = useSelector((state: YambaRootState) => state.questionHighlights);
  } catch (error) {
    // No Provider is available so we are probably in the practice submission
    questionHighlights = []; // Fallback value
  }
  const commonContent = '<strong>Add up to 3 comments to a Connect Live queue.</strong>';
  const highlightsRemaining =
    `${commonContent} Use the Connect Live to chat with a specialist about any questions you might have about your
    feedback`;
  const highlightsFull = `${commonContent} Remove another comment from queue to add this one.`;

  const [disableYambaButton, setDisableYambaButton] = useState<boolean>(
    questionHighlights.length >= 3 && !addedToYamba
  );

  useEffect(() => {
    setDisableYambaButton(questionHighlights.length >= 3 && !addedToYamba);
  }, [questionHighlights, addedToYamba]);

  useLayoutEffect(() => {
    // Destroy the existing popover
    let popover = window.bootstrap.Popover.getInstance(popoverRef.current);
    if (popover) {
      popover.dispose();
      // workaround for https://github.com/twbs/bootstrap/issues/37474
      // @ts-ignore: We're messing with BS internals, but rather than polluting the type, we'll just ignore
      popover._activeTrigger = {};
      // @ts-ignore
      popover._element = document.createElement('noscript');
    }

    if (popoverRef.current) {
      // Determine the new content
      const newContent = questionHighlights.length >= 3 ? highlightsFull : highlightsRemaining;

      // Prevent the popover from showing if the comment view is hidden
      $(popoverRef.current).on('show.bs.popover', () => {
        if ($(commentViewExpanedRef.current).css('opacity') === '0') {
          return false;
        }
        return true;
      });

      // Initialize a new popover with the new content
      popover = new window.bootstrap.Popover(popoverRef.current, {
        trigger: 'hover',
        placement: 'top',
        container: 'body',
        content: newContent,
        html: true
      });
    }

    // Cleanup function to destroy popover when component unmounts
    return () => {
      if (popover) {
        popover.dispose();
        // workaround for https://github.com/twbs/bootstrap/issues/37474
        // @ts-ignore: We're messing with BS internals, but rather than polluting the type, we'll just ignore
        popover._activeTrigger = {};
        // @ts-ignore
        popover._element = document.createElement('noscript');
      }
    };
  }, [questionHighlights]);

  // const disableYambaButton = questionHighlights.length >= 3 && !addedToYamba;

  let commentTypeElement: React.ReactNode = null;
  let commentCategoryElement: React.ReactNode = null;
  let highlightElement: React.ReactNode = null;
  const yambaButtonElement = (
    <React.Fragment>
      {(Annotator.showYambaButton || addedToYamba) && (
        <button
          ref={popoverRef}
          type="button"
          aria-label="Connect"
          className={`btn btn-disc btn-primary-outline ${addedToYamba ? 'selected' : ''}`}
          onClick={toggleHighlight}
          disabled={disableYambaButton}
        >
          <SvgIcon icon="raised-hand-with-plus" />
        </button>
      )}
    </React.Fragment>
  );

  const handleParagraphClick = (paragraphIndex: number) => {
    const { timestamp, display_type } = annotation;

    // Find the paragraph by its paragraph index (not array position)
    // Note: Multiple annotations can highlight the same paragraph, so we search by paragraph index only
    const paragraphElement = document.querySelector(
      `[data-paragraph-index="${paragraphIndex}"]`
    ) as HTMLElement;

    if (paragraphElement?.dataset?.timestamp) {
      // Scroll to the specific paragraph
      const paragraphTimestamp = paragraphElement.dataset.timestamp;
      Annotator.scrollTo(matchTimestamp(paragraphTimestamp), true, paragraphIndex?.toString());

      // Select this specific paragraph
      if (display_type === DisplayType.Paragraph) {
        const timestampWithCategory = `${timestamp},${CategoryClassName[snakeToPascalCase(commentCategory)]}`;
        Annotator.selectHighlight(timestampWithCategory, true, paragraphIndex?.toString());
      }
    }
  };

  let summaryAddedClassName = '';

  if (Annotator.portFairyEnabled) {
    commentTypeElement = (
      <React.Fragment>
        {/* Comment type */}
        {commentType && (
          <div className="comment-type d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center gap-2"
                role="button"
                tabIndex={0}
                aria-expanded={isPanelSelected}
                aria-label={commentType}
                onKeyUp={keyboardSelectHighlight}
              >
                {annotation.sources && (
                  <div className={`justify-content-center align-items-center ${commentIcon}`}>
                    <SvgIcon
                      className="icon-sm mb-0 icon-comment-category-use-of-sources"
                      icon="comment-category-use-of-sources"
                    />
                  </div>
                )}
                {annotation.comment && !annotation.sources && ( // only show for comments without citations
                  <div className={`d-flex justify-content-center align-items-center ${commentIcon}`}>
                    <svg className="icon icon-sm mb-0">
                      <use xlinkHref={`#${commentIcon}`} />
                    </svg>
                  </div>
                )}
                <span className="comment-type-content">{commentType}</span>
              </div>
            </div>

            {yambaButtonElement}
          </div>
        )}
      </React.Fragment>
    );

    commentCategoryElement = (
      <React.Fragment>
        {/* Category information */}
        {commentCategory && (
          <div className="category-information u-fontSize12 d-flex justify-content-between align-items-center">
            {commentCategory !== CommentCategory.TaskAndContent && (
              <div className="category-information-content">{capitalizedCommentCategory}</div>
            )}
          </div>
        )}
      </React.Fragment>
    );

    highlightElement = (
      <div className="d-flex align-items-center gap-2">
        {commentType === CommentCategory.TaskAndContent && (
          <div className="task-and-content-block">
            <SvgIcon icon="comment-category-task-and-content" />
          </div>
        )}
        {/* Highlighted text */}
        {annotation.sentences && annotation.sentences.length > 1 ? (
          <MultiParagraphDisplay
            sentences={annotation.sentences}
            paragraphIndices={annotation.paragraph_indices}
            onParagraphClick={handleParagraphClick}
            selectedParagraphIndex={selectedParagraphIndex}
          />
        ) : (
          <span className="annotation highlighted-text">
            {annotationText}
          </span>
        )}
      </div>
    );
    summaryAddedClassName = 'new';
  } else {
    commentCategoryElement = (
      <React.Fragment>
        {/* Category information */}
        {commentCategoryAndType && (
          <div className="category-information d-flex justify-content-between">
            <div className="u-fontSize12 d-flex align-items-center">
              {commentCategoryAndType}
            </div>

            {yambaButtonElement}
          </div>
        )}
      </React.Fragment>
    );

    highlightElement = annotation.sentences && annotation.sentences.length > 1 ? (
      <div className="d-flex flex-column gap-2">
        {annotation.sentences.slice(0, 3).map((text, index) => {
          const paragraphIndex = annotation.paragraph_indices?.[index];
          const isSelected = selectedParagraphIndex !== null &&
            selectedParagraphIndex !== undefined &&
            paragraphIndex !== undefined &&
            selectedParagraphIndex.toString() === paragraphIndex.toString();

          return (
            <div
              key={paragraphIndex !== undefined ? paragraphIndex : `para-${index}`}
              className="multi-paragraph-item d-flex align-items-start gap-2 clickable"
              onClick={(e) => {
                e.stopPropagation();
                handleParagraphClick(paragraphIndex);
              }}
              role="button"
              tabIndex={0}
              aria-label={
                paragraphIndex !== undefined ? `Jump to paragraph ${paragraphIndex + 1}` : 'Jump to paragraph'
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleParagraphClick(paragraphIndex);
                }
              }}
            >
              <div className={`multi-paragraph-icon flex-shrink-0 ${isSelected ? 'selected' : ''}`}>
                <SvgIcon icon="comment-category-task-and-content" />
              </div>
              <div className="multi-paragraph-text-box">
                {text}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <span className="annotation highlighted-text">
        {annotationText}
      </span>
    );
  }

  return (
    <div className={`comment-view-expanded ${summaryAddedClassName}`} ref={commentViewExpanedRef}>
      <div className="comment-view-container d-flex flex-column">
        {inDiscussion && (
          <div className="in-discussion-tag" title="In discussion">
            <SvgIcon icon="forum" />
          </div>
        )}

        {commentTypeElement}
        {commentCategoryElement}

        {viewingAsBellmereQa && showQAButtons && (
          <QAAlertMessage message="Is the below highlighted range correct?" />
        )}

        <div className="panel-heading d-inline-flex bg-transparent">
          <div className="comment-heading d-flex flex-column justify-content-between">
            {highlightElement}

            {restorable && (
              <button
                type="button"
                className="btn btn-info-outline d-flex align-self-start"
                onClick={handleRestoreAnnotation}
                disabled={savingSubmission}
              >
                <SvgIcon icon="undo" />
                Undo
              </button>
            )}
          </div>
        </div>

        {viewingAsBellmereQa && showQAButtons && (
          <div className="text-end">
            <QABooleanButtons
              result={result}
              buttonKey="highlight_range_correct"
              buttonText="correct/incorrect"
              selected={result.highlight_range_correct}
              onUpdate={(r: BellmereQA.AnnotationResult) => qaUpdated(r)}
              readOnly={!bellmereQAEditable}
            />
          </div>
        )}

        {!isEditable && (
          <CommentHelperList
            annotation={annotation as Annotation}
            viewingAsBellmereQa={viewingAsBellmereQa && showQAButtons}
            result={result}
            onBellmereQAResult={(r: BellmereQA.AnnotationResult) => qaUpdated(r)}
            bellmereQAEditable={bellmereQAEditable}
          />
        )}

        {/* Suggested changes */}
        {!isEditable && commentCategory === CommentCategory.SpellingAndGrammar && !!annotation.correctedSentence && (
          <div className="suggested-changes d-flex flex-column gap-1">
            <h5 className="m-0">
              Example
            </h5>

            {viewingAsBellmereQa && showQAButtons && (
              <QAAlertMessage message="Is the below example quality acceptable?" />
            )}

            <p>
              "{annotation.correctedSentence}"
            </p>

            {viewingAsBellmereQa && showQAButtons && (
              <div className="text-end">
                <QABooleanButtons
                  result={result}
                  buttonKey="example_quality_acceptable"
                  buttonText="yes/no"
                  selected={result.example_quality_acceptable}
                  onUpdate={(r: BellmereQA.AnnotationResult) => qaUpdated(r)}
                  readOnly={!bellmereQAEditable}
                />
              </div>
            )}
          </div>
        )}

        {!restorable && (
          <Fragment>
            <CitationPane
              sources={annotation.sources}
              annotationId={annotation.id}
              citeStatus={annotation.citeStatus}
              saveCiteStatus={saveCiteStatus}
              isEditable={isEditable}
              contextWidth={contextWidth}
            />

            <CommentInputPane
              annotation={annotation}
              annotationText={annotationText}
              bulkDeleteMode={bulkDeleteMode}
              commentCategory={commentCategory}
              commentHelpersSummary={commentHelpersSummary}
              confirmingDeletion={confirmingDeletion}
              hasSources={hasSources}
              isEditable={isEditable}
              isEditing={isEditing}
              isEditingDisabled={isEditingDisabled}
              markedForDeletion={markedForDeletion}
              onAssignCommentVariants={assignCommentVariants}
              onCancelEdit={cancelEdit}
              onCancelRemovingAnnotation={onCancelRemovingAnnotation}
              onCitationDelete={removeCitation}
              onEditComment={editComment}
              onMarkedForDeletionChange={onMarkedForDeletionChange}
              onRemoveComment={removeAnnotation}
              onSaveComment={saveComment}
              onStartRemovingAnnotation={onStartRemovingAnnotation}
              savingSubmission={savingSubmission}
              viewingAsBellmereQa={viewingAsBellmereQa && showQAButtons}
              setBellmereQAResult={setBellmereQAResult}
              bellmereQAEditable={bellmereQAEditable}
              result={result}
            />
          </Fragment>
        )}

        <Suspense fallback={<div>Loading...</div>}>
          {viewingAsBellmereQa && showQAButtons && (
            <Fragment>
              <QAAlertMessage message="Any other comments?" />
              <textarea
                className="qa-comments"
                name="comments"
                defaultValue={result.comments}
                placeholder="Write your feedback here..."
                rows={4}
                onBlur={(event) => qaUpdated({ ...result, comments: event.target.value })}
                disabled={!bellmereQAEditable}
              />
            </Fragment>
          )}

          {/* QA */}
          {viewingAsBellmereQa && (
            <QAActionState
              value={result.action}
              onUpdate={(action) => qaUpdated({ ...result, action })}
              readOnly={!bellmereQAEditable}
            />
          )}

          {viewingAsBellmereQa && (
            <div className="qa-next-btn">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => Annotator.deselectHighlights()}
              >
                Save and Close
              </button>
            </div>
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default CommentViewExpanded;
